import { useEffect } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import {
  useAdminCreateGatheringSpot,
  useAdminUpdateGatheringSpot,
  useGetGatheringSpotById,
  useGetGatheringSpotTypesAll,
  useGetRarityTypesAll,
} from '@outdoria/api';
import { Button, Loader, Size } from '@outdoria/ui';
import { GatheringSpot, GatheringSpotType, Rarity } from '@outdoria/types';
import { ActionBar } from '../../containers';
import { Form } from '../../components';
import { useForm } from '../../hooks';
import { CDN_URL } from '../../env';

import s from './GatheringSpotUpsertView.module.scss';

/**
 * Create a new gathering spot.
 */
export const GatheringSpotCreateView = () => {
  const { gathering_spot_type_id } = useParams();
  const { mutate } = useCreate();

  const defaultData = {
    gathering_spot_type_id:
      gathering_spot_type_id as GatheringSpotType['gathering_spot_type_id'],
    name: '',
    description: '',
    thumbnail: '/items/gathering_spots/herb_64.png',
    image: '/items/gathering_spots/herb.png',
    rarity_id: 'common' as Rarity['rarity_id'],
    difficulty: 1,
    stack_max: 1,
    stack_cooldown: 15,
  };

  return (
    <GatheringSpotUpsertView
      defaultData={defaultData}
      actionLabel="Save new gathering spot"
      mutate={mutate}
    />
  );
};

/**
 * Edit the selected gathering spot.
 */
export const GatheringSpotEditView = () => {
  const { gathering_spot_id = 'UNKNOWN' } =
    useParams<GatheringSpot['gathering_spot_id']>();
  const {
    data: gatheringSpots,
    isLoading,
    isError,
  } = useGetGatheringSpotById(gathering_spot_id);

  const { mutate } = useUpdate(gathering_spot_id);

  if (isLoading) return <Loader />;

  if (isError) return <div>I broke :c</div>;

  return (
    <GatheringSpotUpsertView
      defaultData={gatheringSpots}
      actionLabel="Save updated gathering spot"
      mutate={mutate}
    />
  );
};

/**
 * Reused part.
 */
type GatheringSpotUpsertViewProps = {
  defaultData: GatheringSpotNoId;
  actionLabel: string;
  mutate: (form: GatheringSpotNoId) => void;
};
const GatheringSpotUpsertView = ({
  defaultData,
  actionLabel,
  mutate,
}: GatheringSpotUpsertViewProps) => {
  const { gathering_spot_type_id } = defaultData;

  const { data: gatheringSpotTypes } = useGetGatheringSpotTypesAll();
  const { data: rarity } = useGetRarityTypesAll();

  const { register, form, onReset } = useForm<GatheringSpotNoId>(defaultData);

  const gatheringSpotTypesOptions = gatheringSpotTypes.map(
    gatheringSpotType => ({
      value: gatheringSpotType.gathering_spot_type_id,
      label: gatheringSpotType.name,
    }),
  );

  const rarityOptions = rarity.map(rarity => ({
    value: rarity.rarity_id,
    label: rarity.name,
  }));

  const onGatheringSpotsCreate = () => {
    mutate(form);
  };

  return (
    <div className={s.view}>
      <ActionBar
        title={`Gathering spot type: ${gathering_spot_type_id?.toUpperCase()}`}
        buttons={
          <>
            <Button label="Reset form" onClick={onReset} />
            <Button
              label={actionLabel}
              variant={Button.Variant.ACTION}
              onClick={onGatheringSpotsCreate}
            />
          </>
        }
      />

      <div className={s.view__content}>
        <div className={s.view__row}>
          <Form title="Gathering spot properties" icon="Pin" pretty>
            <Form.Select
              label="Gathering spot type"
              disabled
              data={gatheringSpotTypesOptions}
              {...register('gathering_spot_type_id', { required: true })}
            />

            <Form.Input
              label="Name"
              {...register('name', { required: true })}
            />

            <Form.Input
              label="Description"
              {...register('description', { required: true })}
            />

            <Form.Select
              label="Rarity"
              data={rarityOptions}
              {...register('rarity_id', { required: true })}
            />

            <Form.Input
              label="Difficulty modifier"
              type="number"
              min={1}
              max={10}
              step={1}
              {...register('difficulty', { required: true })}
            />

            <Form.Input
              label="Max stacks"
              type="number"
              min={0}
              step={1}
              {...register('stack_max', { required: true })}
            />

            <Form.Input
              label="Stack cooldown (in minutes)"
              type="number"
              min={0}
              step={1}
              {...register('stack_cooldown', { required: true })}
            />
          </Form>

          <Form title="Image" icon="Image" pretty>
            <div className={s.view__form}>
              <Form.Input
                label={`${CDN_URL}...`}
                {...register('image', { required: true })}
              />

              <Form.Image src={`${CDN_URL}${form.image}`} />
            </div>
          </Form>

          <Form title="Thumbnail" icon="Image" pretty>
            <div className={s.view__form}>
              <Form.Input
                label={`${CDN_URL}...`}
                {...register('thumbnail', { required: true })}
              />

              <Form.Image src={`${CDN_URL}${form.thumbnail}`} size={Size.BIG} />
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

const useCreate = () => {
  const { mutate, error, isSuccess } = useAdminCreateGatheringSpot();

  useEffect(() => {
    if (error) toast.error(error?.message ?? 'Something went wrong :c');
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Yay! Gathering spot created :3`);
    }
  }, [isSuccess]);

  return { mutate };
};

const useUpdate = (gathering_spot_id: string) => {
  const { mutate, error, isSuccess } =
    useAdminUpdateGatheringSpot(gathering_spot_id);

  useEffect(() => {
    if (error) toast.error(error?.message ?? 'Something went wrong :c');
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Yay! Gathering spot updated :3`);
    }
  }, [isSuccess]);

  return { mutate };
};

type GatheringSpotNoId = Omit<GatheringSpot, 'gathering_spot_id'>;
